// src/components/ui/ButtonV2.tsx
import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white shadow-sm hover:bg-primary/90 hover:shadow-md',
        secondary: 'bg-secondary/10 text-secondary hover:bg-secondary/20 border border-secondary/20',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:shadow-md hover:bg-destructive/90',
        outline: 'border-2 border-border bg-transparent hover:bg-muted/50 hover:border-muted-foreground/30',
        ghost: 'hover:bg-muted/50 hover:text-foreground',
        active: 'relative bg-muted/30 text-foreground hover:bg-muted/40 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-1 before:rounded-r-full before:bg-primary',
        link: 'text-primary underline-offset-4 hover:underline font-normal p-0 h-auto',
        glass: 'bg-white/10 dark:bg-muted/10 backdrop-blur-md border border-white/20 dark:border-gray-700/20 hover:bg-white/20 dark:hover:bg-gray-900/20',
        glow: 'bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-[0_0_20px_-5px] shadow-primary/50 hover:shadow-[0_0_30px_-5px] hover:shadow-primary/60',
      },
      size: {
        sm: 'h-9 px-3 text-sm rounded-md',         // 36px - slightly smaller
        md: 'h-11 px-4 text-sm rounded-lg',        // 44px - WCAG compliant (default)
        lg: 'h-12 px-6 text-base rounded-lg',      // 48px - large
        xl: 'h-14 px-8 text-lg rounded-xl',        // 56px - extra large
        icon: 'h-11 w-11 rounded-lg',              // 44px - icon button
        'icon-sm': 'h-9 w-9 rounded-md',           // 36px - small icon
        'icon-lg': 'h-12 w-12 rounded-lg',         // 48px - large icon
      },
      rounded: {
        default: '',
        full: 'rounded-full',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: 'default',
    },
  }
);

export interface ButtonV2Props
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  tooltipContent?: string;
}

const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  ({
    className,
    variant,
    size,
    rounded,
    fullWidth,
    loading,
    leftIcon,
    rightIcon,
    children,
    disabled,
    tooltipContent,
    style,
    ...props
  }, ref) => {
    // Force white text for primary, destructive, and glow variants
    const forceWhiteText = variant === 'primary' || variant === 'destructive' || variant === 'glow';

    return (
      <button
        className={cn(
          buttonVariants({ variant, size, rounded, className }),
          fullWidth && 'w-full',
          loading && 'relative'
        )}
        style={forceWhiteText ? { color: 'hsl(var(--primary-foreground))', ...style } : style}
        ref={ref}
        disabled={disabled || loading}
        aria-label={typeof children === 'string' ? children : tooltipContent}
        {...props}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
          </div>
        )}
        <span className={cn(
          'inline-flex items-center gap-2',
          loading && 'opacity-0'
        )}>
          {leftIcon && <span className="shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="shrink-0">{rightIcon}</span>}
        </span>
      </button>
    );
  }
);

ButtonV2.displayName = 'ButtonV2';

export { ButtonV2, buttonVariants };
